import type { GameEvent, Seat } from "@guandan/engine";

export const VOICE_CATALOG_VERSION = "v1";
export const VOICE_IDS = [
  "thalia", "andromeda", "ophelia", "phoebe", "athena", "pandora", "theia",
  "apollo", "aries", "orion", "jupiter", "zeus",
] as const;
export type VoiceId = (typeof VOICE_IDS)[number];

export type SpeechFamily = "play" | "pass" | "hesitate" | "tribute" | "big";
export type SpeechTiming = "lead" | "action" | "live";
export type SpeechDeck = "competitive" | "playful" | "dramatic";

export interface SpeechLine {
  readonly scene?: string;
  readonly lineId: string;
  readonly deck: SpeechDeck;
  readonly family: SpeechFamily;
  readonly text: string;
  readonly durationBudgetMs: number;
}

export interface SpeechCue {
  readonly cueId: string;
  readonly seat: Seat;
  readonly voiceId: VoiceId;
  readonly lineId: string;
  readonly assetId: string;
  readonly timing: SpeechTiming;
  readonly durationBudgetMs: number;
}

/** Optional AI-side selection hint; transport and audio cue shapes stay unchanged. */
export interface SpeechSelectionOverride {
  readonly seat: Seat;
  readonly family: SpeechFamily;
  readonly deck: SpeechDeck;
  readonly probability: number;
}

const play = (texts: readonly string[], deck: SpeechDeck): SpeechLine[] => texts.map((text, index) => ({
  lineId: `${deck}.play.${String(index + 1).padStart(2, "0")}`,
  deck, family: "play", text, durationBudgetMs: 820,
}));
const pass = (texts: readonly string[], deck: SpeechDeck): SpeechLine[] => texts.map((text, index) => ({
  lineId: `${deck}.pass.${String(index + 1).padStart(2, "0")}`,
  deck, family: "pass", text, durationBudgetMs: 700,
}));
const hesitate = (texts: readonly string[], deck: SpeechDeck): SpeechLine[] => texts.map((text, index) => ({
  lineId: `${deck}.hesitate.${String(index + 1).padStart(2, "0")}`,
  deck, family: "hesitate", text, durationBudgetMs: 900,
}));
const tribute = (texts: readonly string[], deck: SpeechDeck): SpeechLine[] => texts.map((text, index) => ({
  lineId: `${deck}.tribute.${String(index + 1).padStart(2, "0")}`,
  deck, family: "tribute", text, durationBudgetMs: 760,
}));
const big = (texts: readonly string[], deck: SpeechDeck): SpeechLine[] => texts.map((text, index) => ({
  lineId: `${deck}.big.${String(index + 1).padStart(2, "0")}`,
  deck, family: "big", text, durationBudgetMs: 1050,
}));

const competitive = [
  ...play([
    "Let’s keep this moving.", "I’ll start the pace.", "A clean answer.", "Stay with me.",
    "That is the line.", "Let’s make it interesting.", "Your turn to answer.", "Read the table.",
    "I like this shape.", "No need to rush.", "A little pressure.", "That works for me.",
    "Keep your eyes open.", "I’m setting the tone.", "Let’s see the reply.", "Right on cue.",
    "A measured move.", "We are still playing.", "I’ll take the lead.", "That felt natural.",
    "You can do better.", "Now we have a game.", "I’m watching closely.", "Nothing wasted.",
    "A tidy little play.", "Let’s build from here.", "I’m not finished.", "That is enough for now.",
    "A useful answer.", "Keep the rhythm.", "I know this pattern.", "Let’s stay sharp.",
    "A quiet statement.", "The table is mine.", "Make me work for it.", "I’ll test that.",
    "Good timing.", "That opens the door.", "A small step forward.", "Let’s continue.",
  ], "competitive"),
  ...pass([
    "I’ll let that go.", "Not this time.", "I’ll wait here.", "That one is yours.",
    "I can be patient.", "I’m saving the answer.", "No need to chase.", "I’ll pass for now.",
    "Let the table turn.", "I’ll keep my powder.", "That is a pass.", "Your move.",
  ], "competitive"),
  ...hesitate([
    "Give me a moment.", "Let me think.", "There is a choice.", "I’m weighing it.",
    "One second.", "The shape is tricky.", "I want this right.", "Still deciding.",
  ], "competitive"),
  ...tribute([
    "A fair exchange.", "Here you go.", "I’ll pay this one.", "Back to you.",
  ], "competitive"),
  ...big([
    "Now that is power.", "Let’s raise the stakes.", "You wanted a challenge.", "Watch this one.",
    "That changes everything.", "I’m turning the table.", "No answer is easy.", "The fireworks start now.",
  ], "competitive"),
] as const;

const playful = [
  ...play([
    "Come on, keep up.", "I brought a little trouble.", "Let’s have some fun.", "That was cute.",
    "I’m feeling generous.", "You saw that coming.", "Let’s dance around it.", "A charming little move.",
    "I can do this all day.", "Don’t blink now.", "You make this easy.", "I’m just warming up.",
    "Try something clever.", "I’m enjoying this.", "That’s a good look.", "Right back at you.",
    "I’ll keep you guessing.", "The fun part begins.", "Let’s make a memory.", "I know you noticed.",
    "You almost had me.", "I’m being polite.", "That was adorable.", "Keep looking at me.",
    "A little closer.", "I saved this for you.", "You bring out my best.", "Don’t get comfortable.",
    "I’m full of surprises.", "That’s my kind of answer.", "Let’s keep flirting with danger.", "I like your confidence.",
    "You make a fine opponent.", "I’ll give you a hint.", "This could get interesting.", "I’m not shy today.",
    "A playful warning.", "I’ll be right here.", "You keep things lively.", "One more little tease.",
  ], "playful"),
  ...pass([
    "I’ll sit this one out.", "You can have it.", "I’m saving my smile.", "Not giving that away.",
    "I’ll watch you work.", "That’s all yours.", "I’m playing coy.", "Maybe next time.",
    "I’ll let you wonder.", "Nope, not yet.", "Keep the spotlight.", "I’ll pass, darling.",
  ], "playful"),
  ...hesitate([
    "You’re making me think.", "Hold on, handsome.", "Give me a second.", "That is tempting.",
    "I’m considering my options.", "You caught my attention.", "Just a little pause.", "Let me surprise you.",
  ], "playful"),
  ...tribute([
    "A little gift.", "Take good care of it.", "Don’t spend it all.", "And back it comes.",
  ], "playful"),
  ...big([
    "Now you have my attention.", "Let’s make this dramatic.", "I hope you’re ready.", "That is a big hello.",
    "The fun just got serious.", "I brought the thunder.", "You asked for exciting.", "Keep that smile ready.",
  ], "playful"),
] as const;

const dramatic = [
  ...play([
    "The pressure begins now.", "You walked into this.", "I will not blink.", "This is my moment.",
    "Feel the difference.", "I came prepared.", "You cannot ignore that.", "The room just changed.",
    "I see your weakness.", "No room for comfort.", "This is a warning.", "You should have known.",
    "I’m taking control.", "The answer is waiting.", "You are under pressure.", "I will make this count.",
    "Let’s test your nerve.", "That was your chance.", "I’m closing the gap.", "The ending is near.",
    "Don’t mistake my calm.", "You will remember this.", "I’m raising the curtain.", "The next move is mine.",
    "This table has a winner.", "You cannot hide forever.", "I’m done being gentle.", "Now watch carefully.",
    "Every card matters.", "The trap is set.", "I’m coming for the lead.", "You made this personal.",
    "No easy answers now.", "The silence is over.", "I’m writing the ending.", "You felt that one.",
    "The pressure is perfect.", "One move from glory.", "I refuse to yield.", "Let the showdown begin.",
  ], "dramatic"),
  ...pass([
    "I’ll yield, for now.", "Take the trick.", "I’m not giving more.", "Enjoy that victory.",
    "This is not over.", "I’ll retreat with purpose.", "Keep your celebration short.", "I choose patience.",
    "You may have this one.", "I’m waiting for the fall.", "A temporary surrender.", "I’ll pass and remember.",
  ], "dramatic"),
  ...hesitate([
    "The stakes are rising.", "I need one breath.", "This choice has teeth.", "I’m reading the ending.",
    "One wrong step.", "The table is tense.", "I will choose carefully.", "Now comes the hard part.",
  ], "dramatic"),
  ...tribute([
    "A costly exchange.", "Take what I offer.", "Remember this payment.", "Return it if you dare.",
  ], "dramatic"),
  ...big([
    "Witness the reversal.", "This is the knockout.", "You cannot answer this.", "The thunder is here.",
    "Everything changes now.", "I’m ending the debate.", "Bow to the pressure.", "The finale belongs to me.",
  ], "dramatic"),
] as const;

const comedy = Object.entries({
  counter: ["Cute bomb.", "Mine’s louder.", "Plot twist!"],
  humbled: ["Spoke too soon.", "I deserved that.", "Well, awkward."],
  jokerBomb: ["Budget? What budget?", "Subtle, right?", "Oops. All jokers."],
  jiefeng: ["Free delivery!", "VIP service!", "Thanks, partner!"],
  firstOut: ["Early checkout!", "Enjoy the meeting!", "My work here’s done."],
}).flatMap(([scene, texts]) => texts.map((text, i): SpeechLine => ({
  lineId: `comedy.${scene}.${i + 1}`, scene, text, deck: "playful", family: "big", durationBudgetMs: 1400,
})));
export const SPEECH_LINES: readonly SpeechLine[] = [...competitive, ...playful, ...dramatic, ...comedy];

const hash = (value: string): number => {
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) h = Math.imul(h ^ value.charCodeAt(i), 16777619);
  return h >>> 0;
};

export function voiceAssignment(seed: string): readonly VoiceId[] {
  const out = [...VOICE_IDS];
  for (let i = out.length - 1; i > 0; i--) {
    const j = hash(`${VOICE_CATALOG_VERSION}:${seed}:${i}`) % (i + 1);
    [out[i], out[j]] = [out[j] as VoiceId, out[i] as VoiceId];
  }
  return out.slice(0, 4) as VoiceId[];
}

function deckFor(seed: string, family: SpeechFamily, dramaticAllowed: boolean): SpeechDeck {
  const roll = hash(`${seed}:deck:${family}`) % 100;
  if (dramaticAllowed && roll < 8) return "dramatic";
  if (roll < 35) return "playful";
  return "competitive";
}

function familyFor(event: GameEvent): { family: SpeechFamily; dramatic: boolean; probability: number } | null {
  if (event.type === "played") {
    if (event.remaining === 0) return { family: "big", dramatic: true, probability: 100 };
    if (event.combo.power > 0) return { family: "big", dramatic: true, probability: 100 };
    const medium = event.combo.length >= 4 || ["straight", "fullHouse", "consecutivePairs", "consecutiveTriples"].includes(event.combo.type);
    return { family: "play", dramatic: medium, probability: medium ? 50 : 25 };
  }
  if (event.type === "passed") return { family: "pass", dramatic: false, probability: 35 };
  if (event.type === "tributePaid" || event.type === "tributeReturned" || event.type === "tributeResisted") return { family: "tribute", dramatic: false, probability: 50 };
  return null;
}

export function lineById(lineId: string): SpeechLine | undefined {
  return SPEECH_LINES.find((line) => line.lineId === lineId);
}

export function speechAssetId(voiceId: VoiceId, lineId: string): string {
  return `${VOICE_CATALOG_VERSION}/${voiceId}/${lineId}`;
}

export function selectSpeechForEvent(
  event: GameEvent,
  seatVoices: readonly VoiceId[],
  seed: string,
  seq: number,
  recentLineIds: readonly string[] = [],
  seatDecks: readonly (SpeechDeck | undefined)[] = [],
  override?: SpeechSelectionOverride,
): SpeechCue[] {
  const category = override === undefined
    ? familyFor(event)
    : { family: override.family, dramatic: override.deck === "dramatic", probability: override.probability };
  if (category === null) return [];
  const seats: Seat[] = override !== undefined ? [override.seat] : "seat" in event
    ? [event.seat]
    : event.type === "tributePaid" || event.type === "tributeReturned" ? [event.from]
    : event.type === "tributeResisted" ? event.seats.slice(0, 2) : [];
  if (seats.length === 0) return [];
  if ((hash(`${seed}:${seq}:roll`) % 100) >= category.probability) return [];
  return seats.map((seat) => {
    const deck = override?.deck ?? seatDecks[seat] ?? deckFor(`${seed}:${seat}`, category.family, category.dramatic);
    const allCandidates = SPEECH_LINES.filter((line) => line.scene === undefined && line.deck === deck && line.family === category.family);
    const candidates = allCandidates.filter((line) => !recentLineIds.includes(line.lineId));
    const pool = candidates.length === 0 ? allCandidates : candidates;
    const line = pool[hash(`${seed}:${seq}:line:${seat}`) % pool.length] as SpeechLine;
    const voiceId = seatVoices[seat] ?? VOICE_IDS[seat];
    const cueId = `${seed}:${seq}:${seat}:${line.lineId}`;
    return {
      cueId, seat, voiceId, lineId: line.lineId, assetId: speechAssetId(voiceId, line.lineId),
      timing: "action" as const, durationBudgetMs: line.durationBudgetMs,
    };
  });
}

export function selectHesitationCue(
  seat: Seat,
  seatVoices: readonly VoiceId[],
  seed: string,
  token: string,
  deckOverride?: SpeechDeck,
): SpeechCue {
  const deck = deckOverride ?? deckFor(`${seed}:${seat}`, "hesitate", false);
  const candidates = SPEECH_LINES.filter((line) => line.deck === deck && line.family === "hesitate");
  const line = candidates[hash(`${seed}:${token}:${seat}:hesitate`) % candidates.length]!;
  const voiceId = seatVoices[seat] ?? VOICE_IDS[seat];
  return {
    cueId: `${seed}:${token}:${seat}:${line.lineId}`,
    seat, voiceId, lineId: line.lineId, assetId: speechAssetId(voiceId, line.lineId),
    timing: "live", durationBudgetMs: line.durationBudgetMs,
  };
}

export function speechCatalogStats(): { total: number; competitive: number; playful: number; dramatic: number } {
  return {
    total: SPEECH_LINES.length,
    competitive: SPEECH_LINES.filter((line) => line.deck === "competitive").length,
    playful: SPEECH_LINES.filter((line) => line.deck === "playful").length,
    dramatic: SPEECH_LINES.filter((line) => line.deck === "dramatic").length,
  };
}

export const isSupportedVoice = (value: string): value is VoiceId => (VOICE_IDS as readonly string[]).includes(value);
export const isSupportedLine = (value: string): boolean => SPEECH_LINES.some((line) => line.lineId === value);
